import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Check, Copy, Download, Pencil, Plus, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ConfirmationDialog } from '@/components/ui/ConfirmationDialog';
import { DataLoader } from '@/components/ui/DataLoader';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useDeleteReportTemplate,
  useFundReportDefinitions,
  useReportTemplates,
  usePutTemplateInUse,
} from '@/hooks/react-query/useFundReports';
import { fundReportService } from '@/services/fundReportService';
import { formatDate } from '@/utils/formatters';
import type {
  ReportDefinition,
  ReportTemplateListItem,
} from '@/types/fundReport';

const download = async (fundId: string, row: ReportTemplateListItem) => {
  const template = await fundReportService.getReportTemplate(fundId, row.id);
  fundReportService.downloadTemplateHtml(template, `${row.name}.html`);
};

function SharedDefaultActions({
  definitions,
  templates,
  disabled,
  onUseShared,
}: {
  definitions: ReportDefinition[];
  templates: ReportTemplateListItem[];
  disabled: boolean;
  onUseShared: (templateId: string) => void;
}) {
  const offers = definitions.flatMap((definition) => {
    const own = templates.filter(
      (row) =>
        row.report_definition_id === definition.id && row.owner === 'fund'
    );
    const shared = templates.find(
      (row) =>
        row.report_definition_id === definition.id && row.owner === 'shared'
    );
    // Nothing to go back to, or the fund is not overriding anything.
    if (!shared || !own.some((row) => row.is_in_use)) return [];
    return [{ definition, sharedId: shared.id }];
  });

  if (offers.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2">
      {offers.map(({ definition, sharedId }) => (
        <Button
          key={definition.id}
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={() => onUseShared(sharedId)}
        >
          Use shared default for {definition.name}
        </Button>
      ))}
    </div>
  );
}

export function ReportTemplatesSection({ fundId }: { fundId: string }) {
  const navigate = useNavigate();
  const templatesQuery = useReportTemplates(fundId);
  const { data: definitions = [] } = useFundReportDefinitions(fundId);
  const putInUse = usePutTemplateInUse(fundId);
  const deleteTemplate = useDeleteReportTemplate(fundId);

  const [pendingDelete, setPendingDelete] =
    useState<ReportTemplateListItem | null>(null);

  const printable = definitions.filter(
    (definition: ReportDefinition) => definition.has_document
  );
  const reportName = (definitionId: string) =>
    printable.find((definition) => definition.id === definitionId)?.name ?? '-';

  const editTemplate = (templateId: string) =>
    navigate(`/app/funds/${fundId}/reports/templates/${templateId}`);

  // A new template starts as a copy of what its report prints today, so the
  // report it belongs to is picked on the editor.
  const addTemplate = (row?: ReportTemplateListItem) => {
    const from = row
      ? `?report=${row.report_definition_id}&from=${row.id}`
      : '';
    navigate(`/app/funds/${fundId}/reports/templates/new${from}`);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Templates</h2>
          <p className="text-sm text-muted-foreground">
            Every report that prints a document has its own templates. Activate
            one, or add your own. Shared templates are read only, but you can
            copy one.
          </p>
        </div>
        <Button size="sm" onClick={() => addTemplate()}>
          <Plus className="mr-2 h-4 w-4" />
          Add template
        </Button>
      </div>

      <DataLoader query={templatesQuery}>
        {(templates: ReportTemplateListItem[]) => {
          const missing = printable.filter(
            (definition) =>
              !templates.some(
                (row) => row.report_definition_id === definition.id
              )
          );

          return (
            <div className="space-y-3">
              <Card>
                <CardContent className="pt-6">
                  {templates.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      No template yet, so no report can print.
                    </p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Report</TableHead>
                          <TableHead>Source</TableHead>
                          <TableHead>Updated</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {templates.map((row) => (
                          <TableRow key={row.id}>
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-2">
                                {row.name}
                                {row.is_in_use && <Badge>Active</Badge>}
                              </div>
                            </TableCell>
                            <TableCell>
                              {reportName(row.report_definition_id)}
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant={
                                  row.owner === 'fund' ? 'secondary' : 'outline'
                                }
                              >
                                {row.owner === 'fund' ? 'This fund' : 'Shared'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {row.updated_at
                                ? formatDate(row.updated_at)
                                : '-'}
                            </TableCell>
                            <TableCell>
                              <div className="flex justify-end gap-1">
                                {row.owner === 'fund' && !row.is_in_use && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    disabled={putInUse.isPending}
                                    onClick={() => putInUse.mutate(row.id)}
                                  >
                                    <Check className="mr-2 h-4 w-4" />
                                    Activate
                                  </Button>
                                )}

                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => void download(fundId, row)}
                                >
                                  <Download className="h-4 w-4" />
                                  <span className="sr-only">Download</span>
                                </Button>

                                {row.owner === 'shared' ? (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => addTemplate(row)}
                                  >
                                    <Copy className="mr-2 h-4 w-4" />
                                    Copy
                                  </Button>
                                ) : (
                                  <>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => editTemplate(row.id)}
                                    >
                                      <Pencil className="mr-2 h-4 w-4" />
                                      Edit
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      disabled={deleteTemplate.isPending}
                                      onClick={() => setPendingDelete(row)}
                                    >
                                      <Trash2 className="h-4 w-4 text-destructive" />
                                      <span className="sr-only">Delete</span>
                                    </Button>
                                  </>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>

              <SharedDefaultActions
                definitions={printable}
                templates={templates}
                disabled={putInUse.isPending}
                onUseShared={(templateId) => putInUse.mutate(templateId)}
              />

              {missing.length > 0 && templates.length > 0 && (
                <p className="text-sm text-muted-foreground">
                  No template yet, so this cannot print:{' '}
                  {missing.map((definition) => definition.name).join(', ')}.
                </p>
              )}
            </div>
          );
        }}
      </DataLoader>

      <ConfirmationDialog
        open={!!pendingDelete}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title="Delete this template?"
        description={
          pendingDelete?.is_in_use
            ? `${pendingDelete.name} is active. Deleting it makes ${reportName(pendingDelete.report_definition_id)} print the shared default again.`
            : `${pendingDelete?.name ?? ''} is removed for good. This cannot be undone.`
        }
        confirmLabel="Delete"
        variant="destructive"
        isLoading={deleteTemplate.isPending}
        onConfirm={() => {
          if (!pendingDelete) return;
          deleteTemplate.mutate(pendingDelete.id);
          setPendingDelete(null);
        }}
      />
    </div>
  );
}
